import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import { cloneDeep } from "lodash";

import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";
import ImportView from "./ImportView";

const userAccountCSVExample = [
  ["Username", "Password", "Description"],
  ["admin_hq", "P@ssw0rd!23", "Headquarters Administrator"],
  ["netadmin", "N3t#2026!", "Network Operations"],
  ["it_support", "SupP0rt@88", ""],
  ["guest_mgr", "Gu3st#WiFi", "Guest Wi-Fi Management"],
];

const UploadView = ({
  viewStateEnum,
  getViewTitle,
  form,
  handleUploadView,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [openImportView, setOpenImportView] = useState(false);
  const [userAccountItemList, setUserAccountItemList] = useState([]);
  const [totalCsvCount, setTotalCsvCount] = useState(0);
  const [replaceFlag, setReplaceFlag] = useState(false);

  function HandleImportView() {
    return {
      closeView: () => {
        setOpenImportView(false);
      },
      updateUserAccountItemsList: (items, totalCount) => {
        setUserAccountItemList(items);
        setTotalCsvCount(totalCount || 0);
      },
    };
  }

  const downloadCSVExample = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + cloneDeep(userAccountCSVExample).map(row => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "user_account_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeView = () => {
    handleUploadView.closeView();
  };

  const displayImportView = () => {
    setOpenImportView(true);
  };

  const checkFormValid = () => {
    return userAccountItemList.length > 0;
  };

  const handleSave = () => {
    if (!checkFormValid()) return;

    handleUploadView.updateUserAccountItemsList(replaceFlag, userAccountItemList);
    closeView();
  };

  let viewHeight = 0;
  let marginTop = 0;
  if (viewRef && viewRef.current) {
    viewHeight = viewRef.current.offsetHeight;
    marginTop = viewHeight / 6;
  }
  const parentViewWidth = viewRef && viewRef.current ? viewRef.current.offsetWidth : 0;
  const parentViewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;

  return (
    <>
      <Box ref={viewRef} style={{ position: "relative", padding: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.UPLOAD)}
          </Typography>
          <Button
            onClick={closeView}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", margin: "20px 10px 10px" }}>
          <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={downloadCSVExample}>
            Download CSV Sample
          </OutlineRectangleButton>
        </Box>

        <Box
          style={{
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: "4px",
            margin: "0 10px 0 20px",
          }}
        >
          <Box sx={{ ...form.getTableStyle(), height: "300px", padding: "0 10px" }}>
            <TableContainer
              component={Paper}
              sx={{ "&.MuiPaper-root": { boxShadow: "none", border: "none" } }}
              style={{ overflowY: "auto" }}
            >
              <Table sx={{ borderCollapse: "collapse", "& td, & th": { border: "none" } }}>
                <TableHead>
                  <TableRow style={{ borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` }}>
                    <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description (Optional)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userAccountItemList.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.username}</TableCell>
                      <TableCell>{item.password}</TableCell>
                      <TableCell>{item.description || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
            marginLeft: "20px",
            marginRight: "10px",
            marginBottom: "20px",
          }}
        >
          <Box sx={form.getLabelStyle()}>
            User Accounts: {userAccountItemList.length} /{totalCsvCount}
          </Box>
          <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={displayImportView}>
            Upload
          </OutlineRectangleButton>
        </Box>

        <Box style={{ marginLeft: "10px", marginBottom: "10px" }}>
          <Checkbox
            icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />}
            checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />}
            checked={replaceFlag}
            onChange={(e) => setReplaceFlag(e.target.checked)}
          />
          <Typography component="span" sx={{ fontSize: theme.typography.customFontSize[2] }}>
            Replace the existing User Account in the list.
          </Typography>
        </Box>

        <Box style={{ marginLeft: "20px", marginBottom: "30px" }}>
          <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>Note:</Typography>
          <Typography sx={{ fontSize: theme.typography.customFontSize[2], marginLeft: "10px" }}>
            1. Each Username, Password and its description on a separate line, with a space between them.
          </Typography>
        </Box>

        <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
          <OutlineButton onClick={closeView} sx={{ marginRight: "20px" }} width="100px" height="32px">
            Cancel
          </OutlineButton>
          <SolidButton onClick={handleSave} width="100px" height="32px" disabled={!checkFormValid()}>
            Import
          </SolidButton>
        </Box>

        {openImportView && (
          <Box
            style={{
              ...form.getMaskStyle(),
              width: parentViewWidth + 40 + "px",
              height: parentViewHeight + 60 + "px",
              transform: "translateY(-20px) translateX(-20px)",
            }}
          />
        )}
      </Box>

      {openImportView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: parentViewWidth + 40 + "px",
            transform: `translateY(${marginTop}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <ImportView
              viewStateEnum={viewStateEnum}
              getViewTitle={getViewTitle}
              form={form}
              handleImportView={HandleImportView()}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default React.memo(UploadView);
